'use client';

import type { LottieComponentProps } from 'lottie-react';
import dynamic from 'next/dynamic';
import { useRef } from 'react';
import { type AnimatedIconName, animatedIcons } from '@/lib/ui-kit/animated-icon-registry';
import { cn } from '@/lib/ui-kit/utils';

export { type AnimatedIconName, animatedIcons };

export interface AnimatedIconProps {
  name: AnimatedIconName;
  size?: number;
  className?: string;
  /** Play animation on hover (default: true) */
  playOnHover?: boolean;
  /**
   * Listen for the hover on the parent element instead of the icon's own box.
   * Icon buttons are larger than their glyph, so a pointer that comes to rest in
   * the padding highlights the button without ever entering the icon — and the
   * animation silently never fires. Opt in wherever the icon sits inside a
   * bigger hit area.
   */
  playOnParentHover?: boolean;
  /** Loop the animation */
  loop?: boolean;
  /** Custom color override via CSS */
  color?: string;
  /** Limit playback to [startFrame, endFrame] segment */
  playSegment?: [number, number];
  /** Additional lottie props */
  lottieProps?: Partial<LottieComponentProps>;
}

// lottie-react drags lottie-web in with it, and the player only ever draws
// after mount (the server renders an empty box either way), so the runtime is
// split out and fetched on the client with no visible difference. The sized
// box below is rendered on the server exactly as the player's container was,
// so nothing shifts when the player arrives.
const AnimatedIconLottie = dynamic(
  () => import('@/lib/ui-kit/animated-icon-lottie').then((mod) => mod.AnimatedIconLottie),
  { ssr: false },
);

export function AnimatedIcon(props: AnimatedIconProps) {
  const { size = 24, className, color } = props;
  const hostRef = useRef<HTMLSpanElement>(null);

  return (
    <span
      ref={hostRef}
      className={cn('animated-icon', className)}
      style={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        display: 'inline-block',
        lineHeight: 0,
        verticalAlign: 'middle',
        flexShrink: 0,
        ...(color ? { color } : {}),
      }}
    >
      <AnimatedIconLottie {...props} hostRef={hostRef} />
    </span>
  );
}
